import { Popper, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; // Using Day.js adapter
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import { Button, ButtonType } from '../Button';
import { TextIconColor } from '../constants';
import { Typography } from '../Typography';

import {
  darkTheme,
  dateTimePickerMuiStyles
} from './DateTimeRangePicker.styles';

export interface DateTimeRange {
  endDate?: Date; // Optional Date, defaults to undefined
  startDate?: Date; // Optional Date, defaults to undefined
}

export interface DateTimeRangePickerProps {
  onDateRangeChange: (dateRange: DateTimeRange) => void;
  value: DateTimeRange | undefined;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  onDateRangeChange,
  value
}) => {
  const [validationError, setValidationError] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [displayText, setDisplayText] = useState<string>('Select a time range');
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  const validate = () => {
    let error = '';
    const startDate = startDateRef.current?.value
      ? new Date(startDateRef.current.value)
      : undefined;
    const endDate = endDateRef.current?.value
      ? new Date(endDateRef.current.value)
      : undefined;

    if (startDate && endDate && startDate > endDate) {
      error = 'End date cannot be before start date';
    }

    setValidationError(error);
    return error.length === 0;
  };

  const updateDisplayText = (dateRange: DateTimeRange) => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (true) {
      case !!(dateRange.startDate && dateRange.endDate):
        setDisplayText(
          `${dateRange.startDate.toLocaleString()} to ${dateRange.endDate.toLocaleString()}`
        );
        break;
      case !!(dateRange.startDate && !dateRange.endDate):
        setDisplayText(`${dateRange.startDate.toLocaleString()} to now`);
        break;
      case !!(!dateRange.startDate && dateRange.endDate):
        setDisplayText(
          `All events until ${dateRange.endDate.toLocaleString()}`
        );
        break;
      default:
        setDisplayText('Select a time range');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const dateRange: DateTimeRange = {
      ...(startDateRef.current?.value && {
        startDate: new Date(startDateRef.current.value)
      }),
      ...(endDateRef.current?.value && {
        endDate: new Date(endDateRef.current.value)
      })
    };
    updateDisplayText(dateRange);

    onDateRangeChange(dateRange);

    setOpen(false);
  };

  const handleClear = () => {
    if (startDateRef.current) {
      startDateRef.current.value = '';
    }
    if (endDateRef.current) {
      endDateRef.current.value = '';
    }
    setValidationError('');
    setDisplayText('Select a time range');
    setOpen(false);

    onDateRangeChange({});
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (value) {
      updateDisplayText(value);
    }
  }, [value]);

  return (
    <ThemeProvider theme={darkTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="z-[1000]" data-testid="date-time-range-picker">
          <Button
            label={displayText}
            onClick={handleButtonClick}
            type={ButtonType.OUTLINED}
          />
          <Popper
            anchorEl={anchorEl}
            open={open}
            placement="bottom-start"
            style={{ zIndex: 9999 }}
          >
            <div
              className="bg-blue-chat-background flex flex-col items-center rounded-lg p-4 shadow-lg"
              data-testid="date-time-picker-container"
            >
              <div
                className="mt-3 flex space-x-4"
                data-testid="date-time-picker-input-container"
              >
                <DateTimePicker
                  defaultValue={
                    value?.startDate ? dayjs(value.startDate) : null
                  }
                  inputRef={startDateRef}
                  label="Start Date"
                  maxDate={dayjs()}
                  sx={dateTimePickerMuiStyles}
                />
                <DateTimePicker
                  defaultValue={value?.endDate ? dayjs(value.endDate) : null}
                  inputRef={endDateRef}
                  label="End Date"
                  maxDate={dayjs()}
                  sx={dateTimePickerMuiStyles}
                />
              </div>

              {validationError && (
                <Typography color={TextIconColor.DESTRUCTIVE}>
                  {validationError}
                </Typography>
              )}

              <div
                className="mt-4 flex w-full justify-between space-x-2"
                data-testid="date-time-picker-button-group"
              >
                {/* Close Button */}
                <Button
                  label="Close"
                  onClick={handleClose}
                  type={ButtonType.OUTLINED}
                />

                <div
                  className="flex gap-2"
                  data-testid="date-time-picker-button-container"
                >
                  {/* Clear Button */}
                  <Button
                    label="Clear"
                    onClick={handleClear}
                    type={ButtonType.OUTLINED}
                  />

                  {/* Apply Button */}
                  <Button
                    label="Apply"
                    onClick={handleSubmit}
                    type={ButtonType.SOLID}
                  />
                </div>
              </div>
            </div>
          </Popper>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DateTimeRangePicker;
